import { Box, Center } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxioLoader } from "./AxioLoader";
import { useAuth } from "../context/useAuth";
import { authApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { AUTH_IDENTITY_COPY } from "../lib/kyc";
import { getGoogleClientId, mountGoogleButton, notifyGoogleLoadError } from "../lib/googleAuth";
import { setSignupSession } from "../lib/googleSignupSession";
import { resolveHomePath } from "../lib/roles";
import { axioTokens } from "../theme";

interface Props {
  onConnected?: () => void;
}

export function GoogleJoinButton({ onConnected }: Props) {
  const { applyTokenUser } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCredential = useCallback(
    async (idToken: string) => {
      setLoading(true);
      try {
        const res = await authApi.google({ id_token: idToken });
        if (res.access_token && res.user) {
          applyTokenUser(res.access_token, res.refresh_token ?? "", res.user);
          navigate(await resolveHomePath(res.user));
          return;
        }
        if (res.status === "pending_approval") {
          notifications.show({
            color: "yellow",
            title: AUTH_IDENTITY_COPY.pendingAccountTitle,
            message: AUTH_IDENTITY_COPY.pendingAccountBody,
          });
          navigate("/join");
          return;
        }
        if (res.needs_role_selection && res.google_signup_token) {
          setSignupSession({ provider: "google", token: res.google_signup_token });
          onConnected?.();
          notifications.show({
            color: "green",
            title: AUTH_IDENTITY_COPY.googleConnectedTitle,
            message: AUTH_IDENTITY_COPY.googleConnectedBody,
          });
          return;
        }
        notifications.show({
          color: "red",
          title: "Google sign-in failed",
          message: res.message ?? "Unexpected response",
        });
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Google sign-in failed";
        notifications.show({ color: "red", title: "Failed", message });
      } finally {
        setLoading(false);
      }
    },
    [applyTokenUser, navigate, onConnected],
  );

  const credentialHandler = useRef(handleCredential);
  useEffect(() => {
    credentialHandler.current = handleCredential;
  }, [handleCredential]);

  useEffect(() => {
    let cancelled = false;
    getGoogleClientId()
      .then((id) => {
        if (!cancelled) setGoogleClientId(id);
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!googleClientId || !buttonRef.current) return;
    let cancelled = false;

    async function mount() {
      try {
        if (!buttonRef.current) return;
        await mountGoogleButton(buttonRef.current, (token) => credentialHandler.current(token));
      } catch (err) {
        if (!cancelled) notifyGoogleLoadError(err);
      }
    }

    void mount();
    return () => {
      cancelled = true;
    };
  }, [googleClientId]);

  if (configLoading) {
    return (
      <Center h="var(--axio-unit-40)">
        <AxioLoader size="sm" />
      </Center>
    );
  }

  if (!googleClientId) {
    return null;
  }

  return (
    <Box className="googleButtonWrap" style={{ position: "relative", minHeight: "var(--axio-unit-40)" }}>
      {loading && (
        <Center
          style={{
            position: "absolute",
            inset: "var(--axio-unit-0)",
            zIndex: "var(--axio-number-one)",
            background: axioTokens.whiteAlpha60,
            backdropFilter: `blur(${axioTokens.dimension.blurSm})`,
            WebkitBackdropFilter: `blur(${axioTokens.dimension.blurSm})`,
            borderRadius: "var(--axio-radius)",
          }}
        >
          <AxioLoader size="sm" />
        </Center>
      )}
      <div ref={buttonRef} style={{ display: "flex", justifyContent: "center" }} />
    </Box>
  );
}
